import type { ReactNode } from 'react';
import {
  Button,
  Caption1,
  Field,
  Input,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { SPACE, SPACE_X } from '../styles/scale';
import { useSurfaceStyles } from '../styles/surfaces';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';

/**
 * The search-and-filter toolbar above every list in the app (#202).
 *
 * Two screens grew their own copy of this — the Dashboard (search, status,
 * company, date range, sort) and the Reviewer Queue (search, company) — with
 * the same three defects in both:
 *
 *  1. **The results blanked while a filter request was in flight.** Both pages
 *     set `phase: 'loading'` on every filter change, which unmounts the table
 *     and replaces it with a spinner, so a keystroke made the thing you were
 *     reading disappear. Wireframe R1 note 3 is explicit: "It never blanks the
 *     table; existing rows stay until results land." `busy` is how that is
 *     honoured — it renders a progress affordance *beside* the controls and
 *     changes nothing below, so the page can keep the previous rows mounted.
 *  2. **Control widths were ad-hoc**, redefined per page. They are owned here.
 *  3. **No way to clear everything** except emptying each control by hand. The
 *     Reviewer Queue had a "Clear filters" button, but only inside its
 *     no-matches state — invisible the moment a filter matched one row.
 *
 * ── Why `children` and not a filter-descriptor DSL ──
 * The two pages need a multiselect `Dropdown`, a single `Dropdown`, two
 * `DatePicker`s and a sort `Dropdown` between them, each with page-specific
 * option sources and selection semantics. A descriptor object would have to
 * grow a case for every one of those and would still be fought the first time a
 * third screen needs something else. This component owns LAYOUT and the CLEAR
 * AFFORDANCE; each page passes its own controls as children and keeps its own
 * state, including its existing 300ms search debounce — moving that here would
 * silently change when requests fire on two shipped screens.
 *
 * ── Never hidden by an empty result ──
 * The bar is rendered by the page above its results, so it survives a search
 * that matches nothing. Wireframe E1 note 3: "The filter controls stay visible,
 * so there is always a way back."
 */

/**
 * Minimum widths, on the 4px grid. Both pages had already converged on these
 * two numbers by hand; the point of naming them is that a fourth filter added
 * to the Dashboard cannot quietly pick a third width.
 *
 * The search box additionally grows into whatever space is left, because it is
 * the one control whose content is free text of unknown length — a dropdown
 * shows a label that was authored to fit.
 */
const SEARCH_MIN_WIDTH = '240px';
const FILTER_MIN_WIDTH = '180px';

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.m,
  },

  /**
   * `flex-end` alignment, not `center`: every child is a `Field`, whose label
   * sits above its control, and the controls only line up with each other if
   * they are aligned by their bottoms. A page passing a `Field` without a label
   * would otherwise float it half a line high.
   */
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: SPACE_X.m,
    // The width contract applies to whatever the page passed, which this
    // component never clones — a direct-child rule reaches them all without
    // touching their props.
    '& > *': { minWidth: FILTER_MIN_WIDTH },
    // Below the breakpoint the row is a column (via `singleColumn`), and a
    // min-width there can only push the bar wider than the canvas.
    '@media (max-width: 700px)': {
      '& > *': { minWidth: 'auto' },
    },
  },

  searchField: { minWidth: SEARCH_MIN_WIDTH, flexGrow: '1' },

  /**
   * A native `<input>` does not inherit `color` from `FluentProvider` the way a
   * `<div>` does — it falls back to the browser's `buttontext`/`fieldtext`
   * system colour, which rendered the Dashboard's stat tiles as blank boxes
   * under `teamsHighContrastTheme` (CLAUDE.md mistake-log entry 54). Setting
   * the token makes all three themes deterministic instead of coincidental.
   */
  nativeControl: { color: tokens.colorNeutralForeground1 },

  /** The status line: busy indicator on the left, clear affordance on the right. */
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: SPACE_X.s,
  },

  busy: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
  },

  hint: { color: tokens.colorNeutralForeground2 },

  clearCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    marginLeft: 'auto',
  },
});

export interface FilterBarSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Visible label — never a placeholder standing in for one. */
  label: string;
}

export interface FilterBarProps {
  search?: FilterBarSearch;
  /** The page's own filter controls, each wrapped in its own `Field`. */
  children?: ReactNode;
  /** How many filters are currently narrowing the results. 0 hides the clear affordance. */
  activeCount?: number;
  onClear?: () => void;
  /** A request is in flight. Shows progress; never removes anything. */
  busy?: boolean;
}

export function FilterBar({
  search,
  children,
  activeCount = 0,
  onClear,
  busy = false,
}: FilterBarProps) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();

  // Only offered when there is something to clear AND somewhere to send it: a
  // button that resets nothing is worse than no button.
  const showClear = activeCount > 0 && onClear !== undefined;

  return (
    <search
      // The native `search` landmark element rather than a bare `<div>` — this
      // is the app's only search region, and a landmark is how a screen-reader
      // user jumps to it without walking the table above. Deliberately NOT a
      // native form element: the keyboard contract (wireframe K1, enforced by
      // `test/keyboardContract.test.ts`) forbids one app-wide, because it would
      // give Enter a submit behaviour with no handler to review.
      aria-label="Search and filters"
      // Announces the in-flight state without any of the controls losing focus,
      // which is what disabling them during a fetch would do.
      aria-busy={busy}
      className={mergeClasses(surfaces.card, responsive.touchTargets, styles.bar)}
    >
      <div className={mergeClasses(styles.controls, responsive.singleColumn)}>
        {search && (
          <Field label={search.label} className={styles.searchField}>
            <Input
              value={search.value}
              placeholder={search.placeholder}
              input={{ className: styles.nativeControl }}
              onChange={(_, data) => search.onChange(data.value)}
            />
          </Field>
        )}
        {children}
      </div>

      {(busy || showClear) && (
        <div className={styles.meta}>
          {busy && (
            // `<output>` carries the `status` role natively — implicitly polite,
            // so the update is announced once it settles rather than
            // interrupting typing. It is a native form element, hence the
            // explicit foreground token (entry 54). The spinner is hidden from
            // the accessibility tree because the sentence beside it says the
            // same thing, and a bare `progressbar` with no value announces
            // nothing useful twice.
            <output className={mergeClasses(styles.busy, styles.nativeControl)}>
              <Spinner size="tiny" aria-hidden="true" />
              <Caption1 className={styles.hint}>Updating results…</Caption1>
            </output>
          )}
          {showClear && (
            <div className={styles.clearCluster}>
              {/* The count is a word-and-number, not a coloured dot: high
                  contrast discards the brand hue entirely, so a tinted badge
                  would be the only signal and would vanish. */}
              <Caption1 className={styles.hint}>
                {activeCount} filter{activeCount === 1 ? '' : 's'} applied
              </Caption1>
              <Button appearance="subtle" className={styles.nativeControl} onClick={onClear}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
      )}
    </search>
  );
}
